import { CSSProperties } from 'react';

/**
 * Developer Cockpit design tokens.
 *
 * Originally the "single source of truth for the cockpit's visual language"
 * (DX-70), but it only ever governed `components/home/` while `App.tsx` and
 * `public/index.html` restated the same hexes as literals — three unlinked
 * copies of the palette.
 *
 * As of DX-275 this file states no colour of its own. It is a typed accessor
 * over the custom properties in `src/theme/tokens.css`, which are ported from
 * the public Developer Portal and are now the one place a brand colour is
 * written down. Its six importers keep working unchanged; what changes is that
 * a value edited in the token layer reaches them, and that the cards follow
 * the light and dark themes instead of being glass on a violet gradient.
 *
 * DX-74 (design polish) still tunes the cockpit here rather than in each
 * widget — it now does so by reaching for a different token.
 *
 * Two values escaped that as of DX-275 and were still written here: the card's
 * resting and hover shadows. `card` was the literal `'none'`, which is why
 * every card on the page was a hairline border on a near-white ground and the
 * cockpit read as areas printed onto the page. Both are tokens now, so the
 * claim above — that this file states nothing of its own — is true rather than
 * nearly true.
 *
 * MUI v4 only — no MUI v5.
 */

/** Reads a custom property; see `src/theme/tokens.css` for the definitions. */
const token = (name: string) => `var(--${name})`;

export const cockpitTokens = {
  color: {
    /** revenexx primary purple — links, active state, the one accent */
    brand: token('ui-primary'),
    /**
     * Brand yellow. A CTA colour, deliberately NOT a link or icon colour:
     * using it as one is most of why the portal read as "Alpha". Kept so a
     * genuine call to action can reach for it.
     */
    accent: token('color-brand-secondary'),
    /**
     * The one button a page wants pressed: violet on light, yellow on dark,
     * as the public portals draw it (DX-352). Not `brand`, which pales in
     * dark mode — right for a link, too weak for the primary action.
     */
    buttonPrimary: token('ui-button-primary-bg'),
    buttonPrimaryHover: token('ui-button-primary-bg-hover'),
    buttonPrimaryText: token('ui-button-primary-text'),
    /** card surface — white in light mode, elevated dark in dark mode */
    surface: token('ui-bg-elevated'),
    /** a slightly recessed surface, e.g. inputs and table heads */
    surfaceStrong: token('ui-bg-muted'),
    border: token('ui-border'),
    borderHover: token('ui-primary'),
    text: token('ui-text'),
    textMuted: token('ui-text-muted'),
    textFaint: token('docs-card-muted'),
  },
  radius: {
    card: token('radius-card'),
    pill: token('radius-pill'),
  },
  shadow: {
    card: token('shadow-card'),
    cardHover: token('shadow-card-hover'),
  },
  /** The spacing steps the cockpit uses, so a change reaches every card. */
  space: {
    xs: token('space-1'),
    sm: token('space-2'),
    md: token('space-3'),
    lg: token('space-4'),
  },
} as const;

/**
 * Baseline card surface shared by cockpit widgets. Spread into a `makeStyles`
 * rule (or an inline style) to get the standard card look.
 *
 * The glass recipe it replaces — a blur over a semi-transparent violet — only
 * worked because the whole page was a violet gradient. Cards on a light
 * content area are opaque with a hairline border, as in the other portals.
 */
export const cockpitCardBase: CSSProperties = {
  background: cockpitTokens.color.surface,
  border: `1px solid ${cockpitTokens.color.border}`,
  borderRadius: cockpitTokens.radius.card,
  boxShadow: cockpitTokens.shadow.card,
} as CSSProperties;
